import React from "react";
import { Link } from "react-router-dom";
import { X, Pencil, Clock, User as UserIcon, MapPin, FileText } from "lucide-react";

const STATUS_LABEL = {
  scheduled: "Awaiting clock-in",
  active: "Currently active",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_STYLE = {
  scheduled: "bg-njas-sage-tint text-njas-sage-hover",
  active: "bg-emerald-50 text-emerald-800",
  completed: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-50 text-red-700",
};

function fmt(dt) {
  if (!dt) return "";
  return new Date(dt).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
function fmtTime(dt) {
  if (!dt) return "";
  return new Date(dt).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * Read-only shift detail view.
 * `canEdit` — show the "Edit" button.
 * `onEdit` — invoked when Edit is pressed (parent switches to ScheduleShiftModal).
 */
export default function ShiftDetailModal({ shift, canEdit, onEdit, onClose }) {
  if (!shift) return null;
  const status = shift.status || "scheduled";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="shift-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${STATUS_STYLE[status] || "bg-gray-100 text-gray-700"}`}>
                {status === "scheduled" ? "Scheduled" : status === "active" ? "Active" : status === "completed" ? "Completed" : "Cancelled"}
              </span>
              {shift.created_from === "roster" && <span className="text-[10px] text-njas-muted">rostered</span>}
            </div>
            <h3 className="font-serif text-2xl text-njas-navy">Shift · {shift.user_name}</h3>
            <div className="text-xs text-njas-muted mt-0.5">{STATUS_LABEL[status] || status}</div>
          </div>
          <button onClick={onClose} data-testid="shift-detail-close" className="p-1 text-njas-muted hover:text-njas-navy"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3 text-sm">
          {shift.scheduled_start && (
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-njas-muted mt-0.5 shrink-0" />
              <div>
                <div className="text-njas-muted text-xs uppercase tracking-wider">Scheduled</div>
                <div className="text-njas-text">{fmt(shift.scheduled_start)} – {fmtTime(shift.scheduled_end)}</div>
                {shift.planned_duration_minutes != null && (
                  <div className="text-xs text-njas-muted">Planned duration: {Math.floor(shift.planned_duration_minutes / 60)}h {shift.planned_duration_minutes % 60}m</div>
                )}
              </div>
            </div>
          )}

          {shift.clock_in && (
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <div className="text-njas-muted text-xs uppercase tracking-wider">Actual</div>
                <div className="text-njas-text">
                  Clocked in {fmt(shift.clock_in)}
                  {shift.clock_out && <> · out {fmtTime(shift.clock_out)}</>}
                </div>
                {shift.duration_minutes != null && (
                  <div className="text-xs text-njas-muted">Worked: {Math.floor(shift.duration_minutes / 60)}h {shift.duration_minutes % 60}m</div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-start gap-2">
            <UserIcon className="w-4 h-4 text-njas-muted mt-0.5 shrink-0" />
            <div>
              <div className="text-njas-muted text-xs uppercase tracking-wider">Worker</div>
              <div className="text-njas-text">{shift.user_name} <span className="text-xs text-njas-muted capitalize">({(shift.user_role || "").replace("_", " ")})</span></div>
            </div>
          </div>

          {shift.client_id && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-njas-muted mt-0.5 shrink-0" />
              <div>
                <div className="text-njas-muted text-xs uppercase tracking-wider">Client</div>
                <Link to={`/dashboard/clients/${shift.client_id}`} className="text-njas-navy hover:underline" data-testid="shift-detail-client-link" onClick={onClose}>{shift.client_name}</Link>
              </div>
            </div>
          )}

          {shift.notes && (
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-njas-muted mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-njas-muted text-xs uppercase tracking-wider">Notes</div>
                <div className="text-njas-text whitespace-pre-wrap">{shift.notes}</div>
              </div>
            </div>
          )}

          {shift.scheduled_by_name && (
            <div className="text-xs text-njas-muted pt-1">
              Rostered by {shift.scheduled_by_name}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[var(--njas-border)]">
          <button onClick={onClose} className="rounded-full px-5 py-1.5 text-sm text-njas-muted hover:text-njas-navy">Close</button>
          {canEdit && status === "scheduled" && (
            <button onClick={onEdit} data-testid="shift-detail-edit-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-njas-navy text-white px-5 py-1.5 text-sm hover:bg-njas-navy-hover">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
